/**
 * @license Copyright (c) 2023-2025, CKSource Holding sp. z o.o. All rights reserved.
 * For licensing, see LICENSE.md.
 */

import { type ReactNode, useContext } from 'react';
import type { SnippetsSectionProps } from '../Snippets/types';
import { Text } from './Text';
import { SnippetsSection } from '../Snippets/SnippetsSection';
import { Box, Button } from '@mui/material';
import { SkeletonLoader } from '../SkeletonLoader/SkeletonLoader';
import { SECTION_NAME_EDIT_IN_STACKBLITZ } from '../../../constants/snippetsSectionNames';
import { OutputBanner } from '../OutputBanner/OutputBanner';
import { CustomerPortalContext } from '../../../contexts/CustomerPortalContext';
import { InstallationSettingsContext } from '../../../contexts/InstallationSettingsContext';

const stackBlitzPremiumWarn = 'Due to safety reasons, editing in StackBlitz is not supported yet.';
const stackBlitzDescription = 'Opening a project in StackBlitz enables instant editing, ' +
	'running and collaboration in an online development environment.';

export function EditInStackBlitz( {
	editStackBlitzHandler,
	hasPremiumPlugins,
	isLoading,
	number,
	arePremiumFeaturesSelected,
	allFeaturesAvailableInPlan
}: SnippetsSectionProps ): ReactNode {
	const { selectedInstallMethod, selectedCodeOutput } = useContext( InstallationSettingsContext );
	const { isLoggedIn } = useContext( CustomerPortalContext );

	return (
		<SkeletonLoader number={ number } sectionTitle={ SECTION_NAME_EDIT_IN_STACKBLITZ } isLoading={ isLoading }>
			<SnippetsSection headerCharacter={ number } title={ SECTION_NAME_EDIT_IN_STACKBLITZ } hideHorizontalLine={ hasPremiumPlugins }>
				<OutputBanner
					arePremiumFeaturesSelected={ arePremiumFeaturesSelected }
					allFeaturesAvailableInPlan={ allFeaturesAvailableInPlan }
					installMethod={ selectedInstallMethod }
					codeOutput={ selectedCodeOutput }
				/>

				<Box
					sx={ {
						border: '1px solid',
						borderColor: 'secondary.100',
						justifyContent: 'center',
						textAlign: 'center',
						display: 'flex',
						flexFlow: 'column',
						gap: '10px',
						padding: '20px',
						marginBottom: '20px',
						borderRadius: '5px'
					} }
				>
					<Text dataTestId={ 'edit-in-stackblitz-description' }>
						<StackBlitzDescription
							hasPremiumPlugins={ hasPremiumPlugins }
							isLoggedIn={ isLoggedIn }
						/>
					</Text>
					<Button
						variant={ 'outlined' }
						sx={ { alignSelf: 'center' } }
						onClick={ editStackBlitzHandler }
						disabled={ isLoggedIn ? true : hasPremiumPlugins }
					>
						Open in StackBlitz
					</Button>
				</Box>
			</SnippetsSection>
		</SkeletonLoader>
	);
}

function StackBlitzDescription( { hasPremiumPlugins, isLoggedIn }: { hasPremiumPlugins: boolean; isLoggedIn: boolean } ): ReactNode {
	if ( isLoggedIn || hasPremiumPlugins ) {
		return stackBlitzPremiumWarn;
	}

	return stackBlitzDescription;
}
